import type { LocalizedOptionList, OptionGroups } from '../hooks/localization/tOptions';

/**
 * The value codes behind the event option lists, owned here rather than read out of the translation
 * bundles: the codes are what an event stores (focusArea, primaryActivity, sponsors) and what the
 * labels are keyed by, so they must not depend on a language pack having loaded. The i18next key and
 * the ARB id prefix travel with each list; a contract test pins every code below to a label id.
 *
 * The namespace prefix on each key is explicit so a list resolves to its own bundle whatever
 * namespace order the calling component declares.
 */

/**
 * The focus-area groups, in display order. Group ids match the group `value` the legacy `events:focus`
 * list carries, which is also how the header labels are keyed in the bundle (`focus_group_<id>`).
 */
const FOCUS_GROUPS: OptionGroups = {
  idPrefix: 'focus_group',
  items: [
    { id: 'dr-group', values: ['disaster-relief'] },
    {
      id: 'education',
      values: ['stem', 'career-exploration', 'literacy', 'school-essentials', 'edu-other'],
    },
    { id: 'fs-group', values: ['food-security'] },
    {
      id: 'other',
      values: ['environment-and-sustainability', 'housing', 'health', 'animal-welfare', 'other'],
    },
  ],
};

export const FOCUS_AREAS: LocalizedOptionList = {
  i18nKey: 'events:focus',
  idPrefix: 'focus_zone',
  groups: FOCUS_GROUPS,
  values: FOCUS_GROUPS.items.flatMap((group) => group.values),
};

/**
 * The primary-activity groups, in display order. Unlike the focus groups, the legacy `events:activity`
 * groups carry no `value`, so the ids here are the slugs the header labels are keyed under in the
 * bundle (`activity_group_<id>`) and exist nowhere in the legacy source.
 */
const PRIMARY_ACTIVITY_GROUPS: OptionGroups = {
  idPrefix: 'activity_group',
  items: [
    { id: 'general-volunteering', values: ['general-community-service', 'packing-assembly'] },
    {
      id: 'skills-based-volunteering',
      values: [
        'mentoring-tutoring',
        'career-workforce-support',
        'nonprofit-board-service',
        'professional-services',
      ],
    },
  ],
};

export const PRIMARY_ACTIVITIES: LocalizedOptionList = {
  i18nKey: 'events:activity',
  idPrefix: 'activity_zone',
  groups: PRIMARY_ACTIVITY_GROUPS,
  values: PRIMARY_ACTIVITY_GROUPS.items.flatMap((group) => group.values),
  // The legacy list carries a description per activity, so the ARB list reads them too and both
  // states of the flag show the same thing.
  withDescriptions: true,
};

/**
 * T-shirt sizes offered in the complex-signup dialog. The value codes are the numeric
 * `tShirtSizeId`s the API stores (`@range(min: 1, max: 8)` in signupModels.smithy); the labels are
 * keyed `eventDetails_shiftDisplay_complexSignup_tshirtSizes_<id>` in the common bundle. Lives in the
 * default `translation` namespace, so the i18nKey carries no `ns:` prefix.
 */
export const TSHIRT_SIZES: LocalizedOptionList = {
  i18nKey: 'eventDetails.shiftDisplay.complexSignup.tshirtSizes',
  idPrefix: 'eventDetails_shiftDisplay_complexSignup_tshirtSizes',
  values: ['1', '2', '3', '4', '5', '6', '7', '8'],
};

export const ELG_SPONSORS: LocalizedOptionList = {
  i18nKey: 'attendance:sponsors',
  idPrefix: 'sponsors',
  values: [
    'amazonpwd',
    'awe',
    'arabs@',
    'aata',
    'ben',
    'bpp',
    'caribbean-connection',
    'connect@',
    'families@',
    'glamazon',
    'hindus@',
    'indigenous@',
    'jewish@',
    'latinos@',
    'mhw@',
    'sustainability',
    'inclusion', // labelled "Together at Amazon"; code kept for reporting (cce-7071)
    'trans@',
    'warriors@',
    'wifi',
    'women@',
  ],
};

/**
 * Days of the week for the search filters. A `translation`-namespace list resolving through
 * NS_TO_BUNDLES.translation, which carries the options.daysOfWeek bundle. Value codes are the day
 * indices (0 = Sunday) the filters store in the days_of_week URL param, matching the i18next
 * daysOfWeekOptions list.
 */
export const DAYS_OF_WEEK: LocalizedOptionList = {
  i18nKey: 'translation:daysOfWeekOptions',
  idPrefix: 'daysOfWeekOptions',
  values: ['0', '1', '2', '3', '4', '5', '6'],
};

/**
 * The location-type choices on the edit-event location field. Flat list; its value codes
 * (`virtual` / `on-campus` / `off-campus`) match the `locationType_*` ids already in the events
 * bundle. Note these are the edit-event form codes, distinct from the LocationType API enum.
 */
export const LOCATION_TYPES: LocalizedOptionList = {
  i18nKey: 'events:locationType',
  idPrefix: 'locationType',
  values: ['virtual', 'on-campus', 'off-campus'],
};

/**
 * The timezone picker on the event location field: a two-level grouped Select whose region headers
 * and zone labels (with UTC offsets baked into the label) are keyed in the `options.timezones`
 * bundle — `timezones_group_<id>` for the region headers and `timezones_zone_<value>` for the zones.
 * Group ids and zone codes (IANA ids) are owned here in display order, matching the legacy
 * `events:timezones` grouped list. The `events` namespace maps to both `website.events` and
 * `options.timezones` (see NS_TO_BUNDLES), so the flag-on ARB read resolves these labels from
 * `options.timezones`; the flag-off legacy read still comes from the i18next `events` source (en.json).
 */
const TIMEZONE_GROUPS: OptionGroups = {
  idPrefix: 'timezones_group',
  items: [
    {
      id: 'united-states-canada',
      values: [
        'Pacific/Honolulu',
        'America/Anchorage',
        'America/Los_Angeles',
        'America/Denver',
        'America/Chicago',
        'America/New_York',
        'America/Halifax',
      ],
    },
    { id: 'latin-america', values: ['America/Mexico_City', 'America/Bogota', 'America/Sao_Paulo'] },
    { id: 'europe', values: ['Europe/London', 'Europe/Paris', 'Europe/Helsinki', 'Europe/Moscow'] },
    {
      id: 'middle-east-africa',
      values: ['Asia/Dubai', 'Asia/Riyadh', 'Africa/Nairobi', 'Africa/Johannesburg'],
    },
    { id: 'south-asia', values: ['Asia/Kolkata', 'Asia/Dhaka', 'Asia/Karachi'] },
    {
      id: 'east-southeast-asia',
      values: ['Asia/Bangkok', 'Asia/Singapore', 'Asia/Hong_Kong', 'Asia/Tokyo'],
    },
    {
      id: 'pacific-oceania',
      values: ['Australia/Perth', 'Australia/Darwin', 'Australia/Sydney', 'Pacific/Auckland'],
    },
  ],
};

export const TIMEZONES: LocalizedOptionList = {
  i18nKey: 'events:timezones',
  idPrefix: 'timezones_zone',
  groups: TIMEZONE_GROUPS,
  values: TIMEZONE_GROUPS.items.flatMap((group) => group.values),
};

/**
 * The ERG/affinity-group sponsors offered on the create/edit-event Coordinators & Partners step.
 * Distinct from ELG_SPONSORS above: this list lives in the events bundle under
 * `eventDetails.erg_sponsorsOptions` and its value codes differ from the attendance `sponsors` list
 * (e.g. `asian@` here vs `aata` there, `amazon-pwd` vs `amazonpwd`), so it is keyed separately.
 * The ARB ids are the auto-derived form of the i18nKey (`eventDetails.erg_sponsorsOptions` ->
 * `eventDetails_erg_sponsorsOptions`), already present in the events bundle.
 */
export const ERG_SPONSORS: LocalizedOptionList = {
  i18nKey: 'events:eventDetails.erg_sponsorsOptions',
  idPrefix: 'eventDetails_erg_sponsorsOptions',
  values: [
    'amazon-pwd',
    'amazon-women-in-engineer',
    'arabs@',
    'asian@',
    'ben',
    'bpp',
    'caribbean-connection',
    'connect@',
    'families@',
    'glamazon',
    'hindus@',
    'indigenious',
    'jewish@',
    'latinos@',
    'mhw',
    'sustainability',
    'inclusion-ambassador-program', // labelled "Together at Amazon"; code kept for reporting (cce-7071)
    'trans@',
    'warriors@',
    'wifi',
    'women@',
  ],
};
